import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons/faEdit';
import { faPlusSquare } from '@fortawesome/free-solid-svg-icons/faPlusSquare';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons/faTrashAlt';

import Card from 'components/Account/Common/Card/Card';
import { fetcherWithBody } from 'services/fetcher';
import { getRoutesUri } from 'services/generic';
import { useCustomer } from 'hooks/useCustomer';
import useEnv from 'hooks/useEnv';
import useCurrentCountry from 'hooks/useCurrentCountry';

import styles from './AddressInformation.module.scss';

function AddressInformation(): JSX.Element {
  const { t } = useTranslation(['summaryPage']);
  const { customer, refreshCustomer } = useCustomer();
  const { P: primaryAddress, S: secondaryAddress } = customer?.addresses ?? {};
  const labelUk = t('LABEL_UNITED_KINGDOM', { ns: 'translation' });
  const labelFr = t('LABEL_FRANCE', { ns: 'translation' });
  const { urlApiCustomer } = useEnv();
  const { isFr } = useCurrentCountry();

  const labels = [
    {
      name: 'genderFirstAndLastName',
      libelle: `${customer?.civility} ${customer?.firstName} ${customer?.lastName}`,
    },
    {
      name: 'street',
      libellePrimary: primaryAddress?.street,
      libelleSecondary: secondaryAddress?.street,
    },
    {
      name: 'nameComplement',
      libellePrimary: primaryAddress?.nameComplement,
      libelleSecondary: secondaryAddress?.nameComplement,
    },
    {
      name: 'nameAddress',
      libellePrimary: primaryAddress?.nameAddress,
      libelleSecondary: secondaryAddress?.nameAddress,
    },
    {
      name: 'saidPlace',
      libellePrimary: primaryAddress?.saidPlace,
      libelleSecondary: secondaryAddress?.saidPlace,
    },
    {
      name: 'postCodeAndCity',
      libellePrimary: `${primaryAddress?.postalCode} ${primaryAddress?.city}`,
      libelleSecondary: `${secondaryAddress?.postalCode} ${secondaryAddress?.city}`,
    },
    {
      name: 'country',
      libellePrimary: primaryAddress?.country === 'UK' ? labelUk : labelFr,
      libelleSecondary: secondaryAddress?.country === 'UK' ? labelUk : labelFr,
    },
  ];

  const deleteOptionalAddress = () => {
    const body = {
      retarg: customer?.afinetId,
      nameAddress: null,
      nameComplement: null,
      street: null,
      lieudit: null,
      postalCode: null,
      locality: null,
      nom: '',
    };

    fetcherWithBody(`${urlApiCustomer}/address/secondary/save`, body).then(() =>
      refreshCustomer()
    );
  };

  return (
    <Card classes={{ container: styles.container }}>
      <div className={styles.title}>
        <label>{t('LABEL_SHIPPINGADDRESSE_TITLE')}</label>
      </div>
      <div className={styles.containerDelivery}>
        <div className={styles.deliveryAddress}>
          <span className={styles.subTitle}>
            {t('LABEL_SHIPPINGADDRESSE_MAINADDRESSE_TITLE')}
          </span>
          <div className={styles.informationDelivery}>
            {labels.map(label => {
              return (
                <label key={label.name}>
                  {label.libellePrimary || label.libelle}
                </label>
              );
            })}
          </div>
          <div className={styles.deliveryAction}>
            <Link
              to={getRoutesUri('customerAccountAddressMainEdit') as string}
              className={styles.linkContainer}
            >
              <FontAwesomeIcon icon={faEdit} className={styles.editIcon} />
              <span className={styles.linkText}>
                {t('LABEL_SHIPPINGADDRESSE_MAINADDRESSE_EDIT')}
              </span>
            </Link>
          </div>
        </div>
        {customer?.overseas !== '1' && (
          <div className={styles.deliverySecondaryAddress}>
            <span className={styles.subTitle}>
              {t('LABEL_OPTIONAL_ADDRESS')}
            </span>
            {!isFr && <span>**</span>}
            {!secondaryAddress && (
              <div className={styles.informationDelivery}>
                <label>{t('LABEL_OPTIONAL_ADDRESS_CREATE_LABEL')}</label>
              </div>
            )}
            {secondaryAddress && (
              <div className={styles.informationDelivery}>
                {labels.map(label => (
                  <label key={label.name}>
                    {label.libelleSecondary || label.libelle}
                  </label>
                ))}
              </div>
            )}
            <div className={styles.deliveryAction}>
              <Link
                to={
                  getRoutesUri('customerAccountAddressSecondaryEdit') as string
                }
                className={styles.linkContainer}
              >
                <FontAwesomeIcon
                  icon={!secondaryAddress ? faPlusSquare : faEdit}
                  className={
                    !secondaryAddress ? styles.addIcon : styles.editIcon
                  }
                />
                <span className={styles.linkText}>
                  {t(
                    !secondaryAddress
                      ? 'LABEL_SHIPPINGADDRESSE_OPTIONALADDRESSE_ADD'
                      : 'LABEL_SHIPPINGADDRESSE_OPTIONALADDRESSE_EDIT'
                  )}
                </span>
              </Link>
              {secondaryAddress && (
                <a
                  className={styles.linkContainer}
                  onClick={deleteOptionalAddress}
                >
                  <FontAwesomeIcon
                    icon={faTrashAlt}
                    className={styles.editIcon}
                  />
                  <span className={styles.linkText}>
                    {t('LABEL_SHIPPINGADDRESSE_OPTIONALADDRESSE_DELETE')}
                  </span>
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export default AddressInformation;
